import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Container, PageHeader } from "@/components/shared";
import { toast } from "sonner";
import { Check, Crown, Loader2, Sparkles, Rocket } from "lucide-react";

const TIERS = [
  {
    id: "free",
    name: "Gratuit",
    price: "0 €",
    icon: Sparkles,
    tagline: "Pour découvrir BIZARRE AI.",
    features: [
      "Nombre limité de réponses IA",
      "Nombre limité de scripts",
      "Fonctionnalités limitées",
      "Accès partiel à la bibliothèque",
    ],
    cta: null,
  },
  {
    id: "premium",
    name: "Premium",
    price: "9,99 €",
    period: "/mois",
    icon: Crown,
    highlight: true,
    tagline: "Pour ceux qui veulent convaincre.",
    features: [
      "IA plus généreuse",
      "Plus de styles de réponses",
      "Créateur de contenu",
      "Coach d'argumentation",
      "Débats illimités",
      "Bibliothèque complète",
      "Sauvegarde de l'historique",
      "Aucune publicité",
    ],
    cta: "Choisir Premium",
  },
  {
    id: "pro",
    name: "Pro",
    price: "19,99 €",
    period: "/mois",
    icon: Rocket,
    tagline: "Pour les créateurs intensifs.",
    features: [
      "Utilisation très élevée",
      "Fonctionnalités exclusives",
      "Priorité sur les nouvelles fonctionnalités",
      "Outils avancés pour créateurs de contenu",
      "Tout Premium inclus",
    ],
    cta: "Passer Pro",
  },
];

export default function Premium() {
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [checking, setChecking] = useState(false);

  const poll = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { setChecking(false); toast.error("Vérification du paiement expirée."); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setChecking(false);
        toast.success("Bienvenue 👑 Votre abonnement est actif.");
        await refresh();
        navigate("/premium", { replace: true });
        return;
      }
      if (data.status === "expired") { setChecking(false); toast.error("Session de paiement expirée."); return; }
      setTimeout(() => poll(sessionId, attempts + 1), 2000);
    } catch { setTimeout(() => poll(sessionId, attempts + 1), 2000); }
  }, [refresh, navigate]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) { setChecking(true); poll(sessionId); }
  }, [params, poll]);

  const subscribe = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setBusy(null); }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setBusy(null); }
  };

  const currentPlan = user?.plan || "free";
  const isPaid = currentPlan === "premium" || currentPlan === "pro";
  // Un vrai abonnement Stripe existe uniquement si subscription_id est présent.
  // Les accès Premium offerts (bêta / attribués à la main) n'en ont pas -> pas de portail.
  const hasStripeSub = !!user?.subscription_id;

  return (
    <Container className="max-w-6xl">
      {/* Sales hero */}
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-3xl border border-gold/30 p-8 md:p-12 mb-8 text-center noise"
        style={{ background: "linear-gradient(135deg,#171205,#0a0a0a 60%,#050505)" }}>
        <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.18), transparent 60%)" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] mb-4">
            <Crown size={14} /> Débloquez tout votre potentiel
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-light tracking-tight mb-4">
            Devenez <span className="gold-text">imbattable</span> à l'écrit.
          </h1>
          <p className="text-white/55 max-w-2xl mx-auto text-base md:text-lg">
            Répondez plus vite, argumentez mieux et créez du contenu qui marque les esprits.
            Rejoignez ceux qui ne laissent plus jamais un commentaire sans réponse.
          </p>
        </div>
      </motion.div>

      <PageHeader eyebrow="Abonnement" title="Choisissez votre niveau"
        subtitle="Passez à la vitesse supérieure. Débloquez toute la puissance de BIZARRE AI." />

      {isPaid && (
        <div className="card-surface p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="manage-subscription">
          <div>
            <p className="font-medium flex items-center gap-2"><Crown size={16} className="text-gold" /> Vous êtes abonné {currentPlan === "pro" ? "Pro" : "Premium"}.</p>
            <p className="text-white/45 text-sm mt-0.5">
              {!hasStripeSub
                ? "Accès offert (bêta) — aucun abonnement Stripe payant n'est rattaché à ce compte."
                : user?.cancel_at_period_end
                ? "Votre abonnement se terminera à la fin de la période en cours."
                : user?.payment_state === "past_due"
                  ? "Un paiement a échoué — mettez à jour votre moyen de paiement pour conserver l'accès."
                  : "Gérez votre moyen de paiement, changez d'offre ou résiliez à tout moment."}
            </p>
          </div>
          {hasStripeSub && (
            <button data-testid="manage-subscription-btn" onClick={openPortal} disabled={busy !== null}
              className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 text-gold px-5 py-2.5 text-sm hover:bg-gold/10 transition-colors disabled:opacity-50">
              {busy === "portal" ? <Loader2 className="animate-spin" size={16} /> : <Crown size={15} />} Gérer mon abonnement
            </button>
          )}
        </div>
      )}

      {checking && (
        <div className="card-surface p-4 mb-6 flex items-center gap-3 text-sm">
          <Loader2 className="animate-spin text-gold" size={18} /> Vérification de votre paiement...
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
        {TIERS.map((t, idx) => {
          const Icon = t.icon;
          const isCurrent = currentPlan === t.id;
          return (
            <motion.div key={t.id} data-testid={`tier-${t.id}`}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.1, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className={`relative rounded-2xl p-7 flex flex-col overflow-hidden ${
                t.highlight ? "border border-gold/40 noise" : "card-surface"
              }`}
              style={t.highlight ? { background: "linear-gradient(180deg,#161206,#050505)" } : undefined}>
              {t.highlight && (
                <span className="absolute top-6 right-6 text-[10px] uppercase tracking-widest gold-gradient text-black px-3 py-1 rounded-full font-semibold">
                  Populaire
                </span>
              )}
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${t.highlight ? "gold-gradient" : "bg-white/[0.05]"}`}>
                <Icon size={22} strokeWidth={1.5} className={t.highlight ? "text-black" : "text-gold"} />
              </div>
              <h3 className="font-display text-xl">{t.name}</h3>
              <p className="text-white/40 text-sm mt-1 mb-4">{t.tagline}</p>
              <div className="mb-6">
                <span className="font-display text-3xl">{t.price}</span>
                {t.period && <span className="text-white/40 text-base">{t.period}</span>}
              </div>
              <ul className="space-y-3 mb-7 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check size={16} className={`mt-0.5 shrink-0 ${t.highlight ? "text-gold" : "text-white/40"}`} />
                    <span className={t.highlight ? "text-white/85" : "text-white/60"}>{f}</span>
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <div className="w-full text-center rounded-full py-3 text-sm border border-gold/40 text-gold">
                  Votre offre actuelle
                </div>
              ) : t.cta ? (
                <button data-testid={`subscribe-${t.id}`} onClick={() => subscribe(t.id)} disabled={busy !== null}
                  className={`w-full rounded-full py-3 font-medium transition-opacity flex items-center justify-center gap-2 disabled:opacity-50 ${
                    t.highlight ? "gold-gradient text-black hover:opacity-90" : "bg-white text-black hover:bg-gray-200"
                  }`}>
                  {busy === t.id ? <Loader2 className="animate-spin" size={18} /> : <Icon size={16} />}
                  {busy === t.id ? "Redirection..." : t.cta}
                </button>
              ) : (
                <div className="w-full text-center rounded-full py-3 text-sm border border-white/10 text-white/40">
                  Offre de départ
                </div>
              )}
            </motion.div>
          );
        })}
      </div>

      <p className="text-center text-white/30 text-xs mt-8">
        Paiement sécurisé via Stripe. Résiliable à tout moment.
      </p>
    </Container>
  );
}
